import { SORTABLE_DATE_FORMAT } from "modules/common/constants";
import { MakeMonthName } from "modules/common/models/period";
import moment from "moment";
import {
    FREE_CELL,
    PlanCell,
    PlanColumn,
    PlanPeriod,
    PlanProject,
    PlanRow,
    PlanSnapshot,
} from "./plan-types";

export interface WorkloadRangeResponse {
    periods: WorkloadPeriodResponse[];
    /** Поведение планового табеля из настроек приложения */
    planMode: string;
}

export interface WorkloadPeriodResponse {
    month: number;
    year: number;
    monthWorkDayCount: number;
    monthsWorkHours: number;
    users: WorkloadUserResponse[];
}

export interface WorkloadUserResponse {
    user: {
        id: string;
        label: string;
        name: string;
        position: string;
        department: string;
        nonProduction: boolean;
    };
    days: WorkloadDayResponse[];
}

export interface WorkloadDayResponse {
    day: string;
    dayOfMonth: string;
    isDayOff: boolean;
    units: WorkloadUnitResponse[];
}

export interface WorkloadUnitResponse {
    order: { id: string; name: string; inventoryNumber: number; color: string } | null;
    type: string;
    isWorkedOut: boolean;
    hours: number;
    typeDetails: { name: string; shortName: string } | null;
}

export interface OrderDictionaryLike {
    id: string;
    name: string;
    color: string;
    inventoryNumber: number;
    fullInventoryNumber: string;
}

/**
 * Разворачивает ответ бэка (табель по месяцам) в непрерывную ленту рабочих дней.
 * Выходные и праздники (isDayOff) не попадают в колонки вовсе.
 */
export function buildPlan(
    response: WorkloadRangeResponse,
    orders: OrderDictionaryLike[],
    visibleMonths: number,
    today: moment.Moment = moment().startOf("day")
): PlanSnapshot {
    const periodsResponse = response.periods || [];

    const columns: PlanColumn[] = [];
    const periods: PlanPeriod[] = [];
    const projects = new Map<string, PlanProject>();

    orders.forEach((order) => {
        projects.set(order.id, {
            id: order.id,
            name: order.name,
            color: order.color,
            inventoryNumber: order.inventoryNumber,
            fullInventoryNumber: order.fullInventoryNumber,
        });
    });

    // колонки берём у первого сотрудника — состав дней одинаков для всех
    periodsResponse.forEach((period, periodIndex) => {
        const sample = period.users[0];
        const firstColumn = columns.length;

        (sample ? sample.days : []).forEach((day) => {
            if (day.isDayOff) {
                return;
            }

            const date = moment(day.day, SORTABLE_DATE_FORMAT);

            columns.push({
                day: day.day,
                dayOfMonth: day.dayOfMonth,
                month: period.month,
                year: period.year,
                periodIndex,
                isPast: date.isBefore(today),
                isToday: date.isSame(today, "day"),
                hidden: periodIndex >= visibleMonths,
            });
        });

        periods.push({
            month: period.month,
            year: period.year,
            name: MakeMonthName(period.month),
            workDaysCount: period.monthWorkDayCount,
            workHours: period.monthsWorkHours,
            firstColumn,
            lastColumn: Math.max(firstColumn, columns.length - 1),
        });
    });

    const columnIndex = new Map<string, number>();
    columns.forEach((column, index) => columnIndex.set(column.day, index));

    const rowsMap = new Map<string, PlanRow>();

    periodsResponse.forEach((period) => {
        period.users.forEach((user) => {
            // непроизводственные сотрудники в плане работ по проектам не участвуют;
            // бэк их тоже отсекает, здесь — страховка от рассинхрона данных
            if (user.user.nonProduction) {
                return;
            }

            let row = rowsMap.get(user.user.id);

            if (!row) {
                row = {
                    employerId: user.user.id,
                    label: user.user.label || user.user.name,
                    position: user.user.position,
                    department: user.user.department,
                    cells: columns.map(() => FREE_CELL as PlanCell),
                };
                rowsMap.set(user.user.id, row);
            }

            user.days.forEach((day) => {
                const index = columnIndex.get(day.day);

                if (index === undefined) {
                    return;
                }

                row!.cells[index] = toCell(day, projects);
            });
        });
    });

    return {
        columns,
        periods,
        rows: Array.from(rowsMap.values()),
        projects: Array.from(projects.values()),
    };
}

function toCell(day: WorkloadDayResponse, projects: Map<string, PlanProject>): PlanCell {
    const units = day.units || [];

    if (!units.length) {
        return FREE_CELL;
    }

    // день отсутствия (отпуск, болезнь и т.п.) — привязан к дате, очередью не двигается
    const absence = units.find((unit) => !unit.isWorkedOut);

    if (absence) {
        return {
            kind: "absence",
            type: absence.type,
            shortName: absence.typeDetails ? absence.typeDetails.shortName : "",
        };
    }

    // теперь в дне может быть только один проект; у исторических данных берём первый
    const worked = units.find((unit) => !!unit.order);

    if (!worked || !worked.order) {
        return FREE_CELL;
    }

    if (!projects.has(worked.order.id)) {
        projects.set(worked.order.id, {
            id: worked.order.id,
            name: worked.order.name,
            color: worked.order.color,
            inventoryNumber: worked.order.inventoryNumber,
            fullInventoryNumber: String(worked.order.inventoryNumber).padStart(4, "0"),
        });
    }

    return { kind: "work", orderId: worked.order.id };
}
